import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { Eye, EyeOff, User, Mail, Phone, Lock, ChevronRight, ChevronLeft, CheckCircle, Upload, Building2, CreditCard, FileText, Shield, Car } from 'lucide-react';
// import Icon from '../../components/AppIcon';


const STEPS = [
  { id: 1, label: 'Account', icon: '👤' },
  { id: 2, label: 'KYC', icon: '🪪' },
  { id: 3, label: 'Bank', icon: '🏦' },
  { id: 4, label: 'Review', icon: '✅' },
];

// ─── Step Progress Bar ────────────────────────────────────────────────────────
const StepBar = ({ currentStep }) => (
  <div className="w-full px-4 py-5">
    {/* Desktop */}
    <div className="hidden md:flex items-center justify-between relative">
      <div className="absolute top-5 left-0 right-0 h-0.5 bg-white/10 z-0">
        <div
          className="h-full transition-all duration-500"
          style={{
            width: `${((currentStep - 1) / (STEPS?.length - 1)) * 100}%`,
            background: 'linear-gradient(90deg, #ffd700, #f59e0b)',
          }}
        />
      </div>
      {STEPS?.map((step) => {
        const isCompleted = currentStep > step?.id;
        const isActive = currentStep === step?.id;
        return (
          <div key={step?.id} className="flex flex-col items-center z-10 relative">
            <div
              className="w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold transition-all duration-300 shadow-md"
              style={{
                background: isCompleted
                  ? 'linear-gradient(135deg, #ffd700, #f59e0b)'
                  : isActive ? '#ffd700' : 'rgba(255,255,255,0.08)',
                color: isCompleted || isActive ? '#0a1628' : 'rgba(255,255,255,0.4)',
                border: isActive ? '2px solid #ffd700' : 'none',
                transform: isActive ? 'scale(1.15)' : 'scale(1)',
              }}
            >
              {isCompleted ? '✓' : step?.icon}
            </div>
            <span
              className="mt-2 text-xs font-medium text-center"
              style={{ color: isActive ? '#ffd700' : isCompleted ? '#f59e0b' : 'rgba(255,255,255,0.35)' }}
            >
              {step?.label}
            </span>
          </div>
        );
      })}
    </div>
    {/* Mobile */}
    <div className="flex md:hidden items-center gap-3">
      <div className="flex gap-1 flex-1">
        {STEPS?.map((step) => (
          <div
            key={step?.id}
            className="h-1.5 flex-1 rounded-full transition-all duration-300"
            style={{
              background:
                currentStep > step?.id
                  ? 'linear-gradient(90deg, #ffd700, #f59e0b)'
                  : currentStep === step?.id ? '#ffd700' : 'rgba(255,255,255,0.12)',
            }}
          />
        ))}
      </div>
      <span className="text-sm font-semibold text-white/60 whitespace-nowrap">
        {currentStep}/{STEPS?.length}
      </span>
    </div>
    <div className="flex md:hidden mt-2">
      <span className="text-sm font-bold text-[#ffd700]">
        Step {currentStep}: {STEPS?.[currentStep - 1]?.label}
      </span>
    </div>
  </div>
);

// ─── Input Component ──────────────────────────────────────────────────────────
const Input = ({ label, icon: Icon, error, ...props }) => (
  <div className="space-y-1.5">
    {label && <label className="block text-sm font-medium text-white/70">{label}</label>}
    <div className="relative">
      {Icon && (
        <div className="absolute left-3.5 top-1/2 -translate-y-1/2 text-white/30">
          <Icon size={16} />
        </div>
      )}
      <input
        className={`w-full ${Icon ? 'pl-10' : 'pl-4'} pr-4 py-3 rounded-xl bg-white/5 border text-white placeholder-white/25 text-sm transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-[#ffd700]/40 focus:border-[#ffd700]/50`}
        style={{ borderColor: error ? 'rgba(248,113,113,0.5)' : 'rgba(255,255,255,0.1)' }}
        {...props}
      />
    </div>
    {error && <p className="text-xs text-red-400">{error}</p>}
  </div>
);

// ─── Step 1: Account Details ──────────────────────────────────────────────────
const AccountStep = ({ data, onChange, errors }) => {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-xl font-bold text-white">Create Your Host Account</h2>
        <p className="text-sm text-white/50 mt-1">Start earning by listing your vehicle on OnTheGo</p>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Input
          label="Full Name"
          icon={User}
          placeholder="Jane Doe"
          value={data?.fullName}
          onChange={(e) => onChange('fullName', e?.target?.value)}
          error={errors?.fullName}
        />
        <Input
          label="Phone Number"
          icon={Phone}
          placeholder="+254 7XX XXX XXX"
          value={data?.phone}
          onChange={(e) => onChange('phone', e?.target?.value)}
          error={errors?.phone}
        />
      </div>
      <Input
        label="Email Address"
        icon={Mail}
        type="email"
        placeholder="you@example.com"
        value={data?.email}
        onChange={(e) => onChange('email', e?.target?.value)}
        error={errors?.email}
      />
      <div className="space-y-1.5">
        <label className="block text-sm font-medium text-white/70">Password</label>
        <div className="relative">
          <div className="absolute left-3.5 top-1/2 -translate-y-1/2 text-white/30"><Lock size={16} /></div>
          <input
            type={showPassword ? 'text' : 'password'}
            placeholder="Min. 8 characters"
            value={data?.password}
            onChange={(e) => onChange('password', e?.target?.value)}
            className="w-full pl-10 pr-10 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-white/25 text-sm focus:outline-none focus:ring-2 focus:ring-[#ffd700]/40 focus:border-[#ffd700]/50 transition-all duration-200"
          />
          <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3.5 top-1/2 -translate-y-1/2 text-white/30 hover:text-white/60">
            {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
          </button>
        </div>
        {errors?.password && <p className="text-xs text-red-400">{errors?.password}</p>}
      </div>
      <div className="space-y-1.5">
        <label className="block text-sm font-medium text-white/70">Confirm Password</label>
        <div className="relative">
          <div className="absolute left-3.5 top-1/2 -translate-y-1/2 text-white/30"><Lock size={16} /></div>
          <input
            type={showConfirm ? 'text' : 'password'}
            placeholder="Repeat password"
            value={data?.confirmPassword}
            onChange={(e) => onChange('confirmPassword', e?.target?.value)}
            className="w-full pl-10 pr-10 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-white/25 text-sm focus:outline-none focus:ring-2 focus:ring-[#ffd700]/40 focus:border-[#ffd700]/50 transition-all duration-200"
          />
          <button type="button" onClick={() => setShowConfirm(!showConfirm)} className="absolute right-3.5 top-1/2 -translate-y-1/2 text-white/30 hover:text-white/60">
            {showConfirm ? <EyeOff size={16} /> : <Eye size={16} />}
          </button>
        </div>
        {errors?.confirmPassword && <p className="text-xs text-red-400">{errors?.confirmPassword}</p>}
      </div>
      <div className="p-3.5 rounded-xl bg-[#ffd700]/8 border border-[#ffd700]/15 flex items-start gap-3">
        <Car size={16} className="text-[#ffd700] mt-0.5 flex-shrink-0" />
        <p className="text-xs text-white/60 leading-relaxed">
          As a host, you can list your vehicles and earn passive income. Your account will be reviewed after KYC verification.
        </p>
      </div>
    </div>
  );
};

// ─── Step 2: KYC Verification ─────────────────────────────────────────────────
const KYCStep = ({ data, onChange, errors }) => {
  const handleFileChange = (field, file) => {
    if (file) onChange(field, file);
  };

  const FileUploadBox = ({ label, field, accept = 'image/*' }) => (
    <div className="space-y-1.5">
      <label className="block text-sm font-medium text-white/70">{label}</label>
      <label className="flex flex-col items-center justify-center gap-2 p-4 rounded-xl border-2 border-dashed cursor-pointer transition-all duration-200 hover:border-[#ffd700]/40 hover:bg-[#ffd700]/5"
        style={{ borderColor: data?.[field] ? 'rgba(52,211,153,0.4)' : 'rgba(255,255,255,0.12)' }}>
        {data?.[field] ? (
          <>
            <CheckCircle size={20} className="text-green-400" />
            <span className="text-xs text-green-400 font-medium truncate max-w-full px-2">
              {data?.[field]?.name || 'File selected'}
            </span>
          </>
        ) : (
          <>
            <Upload size={20} className="text-white/30" />
            <span className="text-xs text-white/40">Click to upload</span>
          </>
        )}
        <input type="file" accept={accept} className="hidden" onChange={(e) => handleFileChange(field, e?.target?.files?.[0])} />
      </label>
      {errors?.[field] && <p className="text-xs text-red-400">{errors?.[field]}</p>}
    </div>
  );

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-xl font-bold text-white">Identity Verification (KYC)</h2>
        <p className="text-sm text-white/50 mt-1">We need to verify your identity to keep the platform safe</p>
      </div>
      <div className="space-y-1.5">
        <label className="block text-sm font-medium text-white/70">ID Type</label>
        <select
          value={data?.idType}
          onChange={(e) => onChange('idType', e?.target?.value)}
          className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white text-sm focus:outline-none focus:ring-2 focus:ring-[#ffd700]/40 focus:border-[#ffd700]/50 transition-all duration-200"
          style={{ colorScheme: 'dark' }}
        >
          <option value="" className="bg-[#0a1628]">Select ID type</option>
          <option value="national_id" className="bg-[#0a1628]">National ID</option>
          <option value="passport" className="bg-[#0a1628]">Passport</option>
          <option value="driving_license" className="bg-[#0a1628]">Driving License</option>
        </select>
        {errors?.idType && <p className="text-xs text-red-400">{errors?.idType}</p>}
      </div>
      <Input
        label="ID Number"
        icon={FileText}
        placeholder="Enter your ID number"
        value={data?.idNumber}
        onChange={(e) => onChange('idNumber', e?.target?.value)}
        error={errors?.idNumber}
      />
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <FileUploadBox label="ID Front Photo" field="idFront" />
        <FileUploadBox label="ID Back Photo" field="idBack" />
      </div>
      <FileUploadBox label="Selfie with ID" field="selfie" />
      <div className="p-3.5 rounded-xl bg-blue-500/8 border border-blue-500/15 flex items-start gap-3">
        <Shield size={16} className="text-blue-400 mt-0.5 flex-shrink-0" />
        <p className="text-xs text-white/60 leading-relaxed">
          Your documents are encrypted and stored securely. KYC verification typically takes 1–2 business days. You can still set up your bank account while waiting.
        </p>
      </div>
    </div>
  );
};

// ─── Step 3: Bank Account Setup ───────────────────────────────────────────────
const BankStep = ({ data, onChange, errors }) => (
  <div className="space-y-4">
    <div>
      <h2 className="text-xl font-bold text-white">Payout Setup</h2>
      <p className="text-sm text-white/50 mt-1">Add your bank or M-Pesa details to receive earnings</p>
    </div>

    <div className="space-y-1.5">
      <label className="block text-sm font-medium text-white/70">Preferred Payout Method</label>
      <div className="grid grid-cols-2 gap-3">
        {['mpesa', 'bank']?.map((method) => (
          <button
            key={method}
            type="button"
            onClick={() => onChange('payoutMethod', method)}
            className="flex items-center gap-2.5 p-3.5 rounded-xl border transition-all duration-200"
            style={{
              borderColor: data?.payoutMethod === method ? '#ffd700' : 'rgba(255,255,255,0.1)',
              background: data?.payoutMethod === method ? 'rgba(255,215,0,0.08)' : 'rgba(255,255,255,0.03)',
            }}
          >
            <span className="text-lg">{method === 'mpesa' ? '📱' : '🏦'}</span>
            <span className="text-sm font-medium" style={{ color: data?.payoutMethod === method ? '#ffd700' : 'rgba(255,255,255,0.6)' }}>
              {method === 'mpesa' ? 'M-Pesa' : 'Bank Transfer'}
            </span>
          </button>
        ))}
      </div>
    </div>

    {data?.payoutMethod === 'mpesa' && (
      <Input
        label="M-Pesa Number"
        icon={Phone}
        placeholder="+254 7XX XXX XXX"
        value={data?.mpesaNumber}
        onChange={(e) => onChange('mpesaNumber', e?.target?.value)}
        error={errors?.mpesaNumber}
      />
    )}

    {data?.payoutMethod === 'bank' && (
      <>
        <div className="space-y-1.5">
          <label className="block text-sm font-medium text-white/70">Bank Name</label>
          <select
            value={data?.bankName}
            onChange={(e) => onChange('bankName', e?.target?.value)}
            className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white text-sm focus:outline-none focus:ring-2 focus:ring-[#ffd700]/40 focus:border-[#ffd700]/50 transition-all duration-200"
            style={{ colorScheme: 'dark' }}
          >
            <option value="" className="bg-[#0a1628]">Select bank</option>
            {['KCB Bank', 'Equity Bank', 'Co-operative Bank', 'Absa Bank', 'Standard Chartered', 'NCBA Bank', 'DTB Bank', 'Family Bank', 'I&M Bank', 'Stanbic Bank']?.map((b) => (
              <option key={b} value={b} className="bg-[#0a1628]">{b}</option>
            ))}
          </select>
          {errors?.bankName && <p className="text-xs text-red-400">{errors?.bankName}</p>}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Input
            label="Account Number"
            icon={CreditCard}
            placeholder="Enter account number"
            value={data?.bankAccountNumber}
            onChange={(e) => onChange('bankAccountNumber', e?.target?.value)}
            error={errors?.bankAccountNumber}
          />
          <Input
            label="Branch (optional)"
            icon={Building2}
            placeholder="e.g. Westlands"
            value={data?.bankBranch}
            onChange={(e) => onChange('bankBranch', e?.target?.value)}
            error={errors?.bankBranch}
          />
        </div>
      </>
    )}

    <div className="p-3.5 rounded-xl bg-[#ffd700]/8 border border-[#ffd700]/15 flex items-start gap-3">
      <CreditCard size={16} className="text-[#ffd700] mt-0.5 flex-shrink-0" />
      <p className="text-xs text-white/60 leading-relaxed">
        Payouts are processed every Monday. Minimum payout is KES 500. Your first payout will be released after your first completed booking.
      </p>
    </div>
  </div>
);

// ─── Step 4: Review & Submit ──────────────────────────────────────────────────
const ReviewStep = ({ data, onChange, errors }) => {
  const ReviewRow = ({ label, value }) => (
    <div className="flex items-start justify-between py-2.5 border-b border-white/5 last:border-0">
      <span className="text-sm text-white/50">{label}</span>
      <span className="text-sm text-white font-medium text-right max-w-[60%] truncate">{value || '—'}</span>
    </div>
  );

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-xl font-bold text-white">Review & Submit</h2>
        <p className="text-sm text-white/50 mt-1">Confirm your details before creating your host account</p>
      </div>
      <div className="rounded-xl bg-white/4 border border-white/8 overflow-hidden">
        <div className="px-4 py-3 bg-white/4 border-b border-white/8">
          <span className="text-xs font-semibold text-[#ffd700] uppercase tracking-wider">Account Details</span>
        </div>
        <div className="px-4">
          <ReviewRow label="Full Name" value={data?.fullName} />
          <ReviewRow label="Email" value={data?.email} />
          <ReviewRow label="Phone" value={data?.phone} />
        </div>
      </div>
      <div className="rounded-xl bg-white/4 border border-white/8 overflow-hidden">
        <div className="px-4 py-3 bg-white/4 border-b border-white/8">
          <span className="text-xs font-semibold text-[#ffd700] uppercase tracking-wider">KYC Verification</span>
        </div>
        <div className="px-4">
          <ReviewRow label="ID Type" value={data?.idType?.replace('_', ' ')?.replace(/\b\w/g, c => c?.toUpperCase())} />
          <ReviewRow label="ID Number" value={data?.idNumber} />
          <ReviewRow label="ID Front" value={data?.idFront?.name || (data?.idFront ? 'Uploaded' : null)} />
          <ReviewRow label="ID Back" value={data?.idBack?.name || (data?.idBack ? 'Uploaded' : null)} />
          <ReviewRow label="Selfie" value={data?.selfie?.name || (data?.selfie ? 'Uploaded' : null)} />
        </div>
      </div>
      <div className="rounded-xl bg-white/4 border border-white/8 overflow-hidden">
        <div className="px-4 py-3 bg-white/4 border-b border-white/8">
          <span className="text-xs font-semibold text-[#ffd700] uppercase tracking-wider">Payout Details</span>
        </div>
        <div className="px-4">
          <ReviewRow label="Method" value={data?.payoutMethod === 'mpesa' ? 'M-Pesa' : 'Bank Transfer'} />
          {data?.payoutMethod === 'mpesa' && <ReviewRow label="M-Pesa Number" value={data?.mpesaNumber} />}
          {data?.payoutMethod === 'bank' && (
            <>
              <ReviewRow label="Bank" value={data?.bankName} />
              <ReviewRow label="Account" value={data?.bankAccountNumber} />
              {data?.bankBranch && <ReviewRow label="Branch" value={data?.bankBranch} />}
            </>
          )}
        </div>
      </div>
      <div className="space-y-3">
        <label className="flex items-start gap-3 cursor-pointer group">
          <div
            className="w-5 h-5 rounded-md border-2 flex items-center justify-center flex-shrink-0 mt-0.5 transition-all duration-200"
            style={{
              borderColor: data?.termsAccepted ? '#ffd700' : 'rgba(255,255,255,0.2)',
              background: data?.termsAccepted ? '#ffd700' : 'transparent',
            }}
            onClick={() => onChange('termsAccepted', !data?.termsAccepted)}
          >
            {data?.termsAccepted && <span className="text-[#0a1628] text-xs font-bold">✓</span>}
          </div>
          <span className="text-sm text-white/60 leading-relaxed">
            I agree to the <span className="text-[#ffd700] underline cursor-pointer">Host Terms of Service</span> and{' '}
            <span className="text-[#ffd700] underline cursor-pointer">Privacy Policy</span>. I confirm all information provided is accurate.
          </span>
        </label>
        {errors?.termsAccepted && <p className="text-xs text-red-400">{errors?.termsAccepted}</p>}
      </div>
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────
const HostSignupScreen = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [errors, setErrors] = useState({});

  const [formData, setFormData] = useState({
    // Account
    fullName: '',
    phone: '',
    email: '',
    password: '',
    confirmPassword: '',
    // KYC
    idType: '',
    idNumber: '',
    idFront: null,
    idBack: null,
    selfie: null,
    // Bank
    payoutMethod: 'mpesa',
    mpesaNumber: '',
    bankName: '',
    bankAccountNumber: '',
    bankBranch: '',
    // Terms
    termsAccepted: false,
  });

  const updateField = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const validateStep = (step) => {
    const errs = {};
    if (step === 1) {
      if (!formData?.fullName?.trim()) errs.fullName = 'Full name is required';
      if (!formData?.phone?.trim()) errs.phone = 'Phone number is required';
      if (!formData?.email?.trim()) errs.email = 'Email is required';
      else if (!/\S+@\S+\.\S+/?.test(formData?.email)) errs.email = 'Enter a valid email';
      if (!formData?.password) errs.password = 'Password is required';
      else if (formData?.password?.length < 8) errs.password = 'Password must be at least 8 characters';
      if (formData?.password !== formData?.confirmPassword) errs.confirmPassword = 'Passwords do not match';
    }
    if (step === 2) {
      if (!formData?.idType) errs.idType = 'Please select an ID type';
      if (!formData?.idNumber?.trim()) errs.idNumber = 'ID number is required';
      if (!formData?.idFront) errs.idFront = 'ID front photo is required';
      if (!formData?.idBack) errs.idBack = 'ID back photo is required';
      if (!formData?.selfie) errs.selfie = 'Selfie with ID is required';
    }
    if (step === 3) {
      if (formData?.payoutMethod === 'mpesa' && !formData?.mpesaNumber?.trim()) {
        errs.mpesaNumber = 'M-Pesa number is required';
      }
      if (formData?.payoutMethod === 'bank') {
        if (!formData?.bankName) errs.bankName = 'Bank name is required';
        if (!formData?.bankAccountNumber?.trim()) errs.bankAccountNumber = 'Account number is required';
      }
    }
    if (step === 4) {
      if (!formData?.termsAccepted) errs.termsAccepted = 'You must accept the terms to continue';
    }
    return errs;
  };

  const goNext = () => {
    const errs = validateStep(currentStep);
    if (Object.keys(errs)?.length > 0) {
      setErrors(errs);
      return;
    }
    setErrors({});
    setCurrentStep((prev) => prev + 1);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const goBack = () => {
    setCurrentStep((prev) => prev - 1);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const uploadFile = async (file, folder) => {
    if (!file) return null;
    const ext = file?.name?.split('.')?.pop();
    const path = `${folder}/${Date.now()}_${Math.random()?.toString(36)?.slice(2)}.${ext}`;
    const { error } = await supabase?.storage?.from('kyc-documents')?.upload(path, file, { contentType: file?.type });
    if (error) throw error;
    const { data } = supabase?.storage?.from('kyc-documents')?.getPublicUrl(path);
    return data?.publicUrl || null;
  };

  const handleSubmit = async () => {
    const errs = validateStep(4);
    if (Object.keys(errs)?.length > 0) {
      setErrors(errs);
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      // 1. Create auth user
      const { data: authData, error: signUpError } = await supabase?.auth?.signUp({
        email: formData?.email,
        password: formData?.password,
        options: {
          data: {
            full_name: formData?.fullName,
            role: 'host',
          },
        },
      });

      if (signUpError) throw signUpError;
      const userId = authData?.user?.id;
      if (!userId) throw new Error('Account creation failed. Please try again.');

      // 2. Upsert user_profiles with host role
      const { error: profileError } = await supabase?.from('user_profiles')?.upsert({
        id: userId,
        email: formData?.email,
        full_name: formData?.fullName,
        phone: formData?.phone,
        role: 'host',
      }, { onConflict: 'id' });

      if (profileError) throw profileError;

      // 3. Upload KYC documents (best-effort — don't block signup if bucket missing)
      let idFrontUrl = null;
      let idBackUrl = null;
      let selfieUrl = null;
      try {
        [idFrontUrl, idBackUrl, selfieUrl] = await Promise.all([
          uploadFile(formData?.idFront, `${userId}/id_front`),
          uploadFile(formData?.idBack, `${userId}/id_back`),
          uploadFile(formData?.selfie, `${userId}/selfie`),
        ]);
      } catch (uploadErr) {
        console.warn('KYC document upload failed (non-blocking):', uploadErr?.message);
      }

      // 4. Insert host_profiles with KYC + bank data
      const { error: hostProfileError } = await supabase?.from('host_profiles')?.upsert({
        user_id: userId,
        id_type: formData?.idType,
        id_number: formData?.idNumber,
        id_front_url: idFrontUrl,
        id_back_url: idBackUrl,
        selfie_url: selfieUrl,
        kyc_status: 'pending',
        kyc_submitted_at: new Date()?.toISOString(),
        bank_name: formData?.bankName || null,
        bank_account_number: formData?.bankAccountNumber || null,
        bank_branch: formData?.bankBranch || null,
        mpesa_number: formData?.mpesaNumber || null,
        payout_method: formData?.payoutMethod,
        terms_accepted: true,
        terms_accepted_at: new Date()?.toISOString(),
      }, { onConflict: 'user_id' });

      if (hostProfileError) throw hostProfileError;

      // 5. Redirect to host dashboard
      navigate('/host-dashboard');
    } catch (err) {
      console.error('Host signup error:', err);
      setSubmitError(err?.message || 'Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderStep = () => {
    switch (currentStep) {
      case 1: return <AccountStep data={formData} onChange={updateField} errors={errors} />;
      case 2: return <KYCStep data={formData} onChange={updateField} errors={errors} />;
      case 3: return <BankStep data={formData} onChange={updateField} errors={errors} />;
      case 4: return <ReviewStep data={formData} onChange={updateField} errors={errors} />;
      default: return null;
    }
  };

  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(135deg, #0a1628 0%, #0d1f3c 50%, #0a1628 100%)' }}>
      {/* Header */}
      <div className="sticky top-0 z-40 shadow-lg" style={{ background: 'rgba(10,22,40,0.95)', backdropFilter: 'blur(20px)', borderBottom: '1px solid rgba(255,215,0,0.1)' }}>
        <div className="max-w-2xl mx-auto px-4">
          <div className="flex items-center justify-between h-14">
            <button
              onClick={() => navigate('/')}
              className="flex items-center gap-2 group"
            >
              <div className="w-8 h-8 rounded-lg flex items-center justify-center font-black text-sm text-[#0a1628] group-hover:scale-110 transition-transform duration-300"
                style={{ background: 'linear-gradient(135deg, #ffd700 0%, #f59e0b 100%)' }}>
                OG
              </div>
              <span className="text-white font-bold text-base hidden sm:block">
                On<span style={{ color: '#ffd700' }}>TheGo</span>
              </span>
            </button>
            <div className="text-center">
              <p className="text-white font-semibold text-sm">Host Registration</p>
              <p className="text-white/40 text-xs">Step {currentStep} of {STEPS?.length}</p>
            </div>
            <button
              onClick={() => navigate('/customer-login-screen')}
              className="text-white/40 text-xs hover:text-white/70 transition-colors duration-200"
            >
              Sign In
            </button>
          </div>
        </div>
      </div>
      {/* Hero Banner */}
      <div className="px-4 py-5" style={{ background: 'linear-gradient(135deg, rgba(255,215,0,0.06) 0%, transparent 100%)' }}>
        <div className="max-w-2xl mx-auto flex items-center gap-4">
          <div className="flex-1">
            <h1 className="text-white font-bold text-lg">Become a Vehicle Host</h1>
            <p className="text-white/50 text-sm mt-0.5">List your car and earn up to KES 80,000/month</p>
            <div className="flex gap-4 mt-2.5">
              {[{ icon: '🚗', label: 'List any vehicle' }, { icon: '💰', label: 'Earn passively' }, { icon: '🛡️', label: 'Fully insured' }]?.map((item) => (
                <div key={item?.label} className="flex items-center gap-1.5">
                  <span className="text-sm">{item?.icon}</span>
                  <span className="text-xs text-white/50">{item?.label}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="w-16 h-16 rounded-2xl flex items-center justify-center text-3xl flex-shrink-0"
            style={{ background: 'rgba(255,215,0,0.1)', border: '1px solid rgba(255,215,0,0.2)' }}>
            🚗
          </div>
        </div>
      </div>
      {/* Step Progress */}
      <div className="max-w-2xl mx-auto px-4">
        <StepBar currentStep={currentStep} />
      </div>
      {/* Form Card */}
      <div className="max-w-2xl mx-auto px-4 pb-16">
        <div className="rounded-2xl border border-white/8 p-6" style={{ background: 'rgba(255,255,255,0.03)', backdropFilter: 'blur(10px)' }}>
          {renderStep()}

          {submitError && (
            <div className="mt-4 p-3.5 rounded-xl bg-red-500/10 border border-red-500/20 flex items-start gap-2.5">
              <span className="text-red-400 text-sm">⚠</span>
              <p className="text-sm text-red-400">{submitError}</p>
            </div>
          )}

          {/* Navigation Buttons */}
          <div className="flex gap-3 mt-6">
            {currentStep > 1 && (
              <button
                type="button"
                onClick={goBack}
                disabled={isSubmitting}
                className="flex items-center gap-2 px-5 py-3 rounded-xl border border-white/15 text-white/70 text-sm font-medium hover:bg-white/5 hover:text-white transition-all duration-200 disabled:opacity-40"
              >
                <ChevronLeft size={16} />
                Back
              </button>
            )}
            <button
              type="button"
              onClick={currentStep < STEPS?.length ? goNext : handleSubmit}
              disabled={isSubmitting}
              className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl font-bold text-sm text-[#0a1628] transition-all duration-300 hover:scale-[1.02] hover:shadow-lg disabled:opacity-60 disabled:cursor-not-allowed disabled:scale-100"
              style={{ background: isSubmitting ? 'rgba(255,215,0,0.5)' : 'linear-gradient(135deg, #ffd700 0%, #f59e0b 100%)' }}
            >
              {isSubmitting ? (
                <>
                  <div className="w-4 h-4 border-2 border-[#0a1628]/30 border-t-[#0a1628] rounded-full animate-spin" />
                  Creating Account...
                </>
              ) : currentStep < STEPS?.length ? (
                <>
                  Continue
                  <ChevronRight size={16} />
                </>
              ) : (
                <>
                  <CheckCircle size={16} />
                  Create Host Account
                </>
              )}
            </button>
          </div>

          {currentStep === 1 && (
            <p className="text-center text-xs text-white/35 mt-4">
              Already have an account?{' '}
              <button onClick={() => navigate('/customer-login-screen')} className="text-[#ffd700] hover:underline">
                Sign in here
              </button>
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default HostSignupScreen;
